import { environment } from '../../environments/environment';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject, Subscription } from 'rxjs';
import { inflate } from 'zlib';

import { Scrap } from '../models';
import { OA } from '../../ngrx/category-types';
import { allBatchFields, allScrapFields } from '../../ngrx/graphql';
import { SET_SCRAP_REPORT_VIEW } from '../../ngrx/actions';

import { AuthenticationService } from './authentication.service';
import { CredentialService } from './credential.service';

const errorCodes = [400, 401, 403, 404];

@Injectable()
export class ScrapService {
  constructor(
    private authService      : AuthenticationService,
    private credentialService: CredentialService,
    private store            : Store<any>
  ) {}

  private invokeScrap(payload: any): Observable<any> {
    return new Observable(observer => {
    
      this.credentialService.lambda.invoke({
        FunctionName: environment.batchFunction,
        Payload: JSON.stringify(payload)
        }, (err, data) => {
          if (err) {
            if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
              this.authService.refreshCredentials();
            }
            observer.error(err);
          } else {
            const output = typeof data.Payload === 'string'
                ? JSON.parse(data.Payload)
                :       data.Payload;
            if(output.gzip){
              const options = {
                windowBits : 15
              };
              inflate(new Buffer(output.body,'base64'), options, (err, data) => {
                if (err) {                    
                  observer.error(err.stack);
                } else {
                  const unzippedOutput = Object.assign({}, output, {
                    body: data.toString()
                  });
                  observer.next(unzippedOutput);
                }
              });
            } else {
              observer.next(output);
            }
          }
        }
      );
    });
  }

  private invokeScrapMutation(payload: any): void {
    this.credentialService.lambda.invoke({
      FunctionName: environment.batchFunction,
      Payload: JSON.stringify(payload)
      }, (err, data) => {
        if (err) {
          if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
            this.authService.refreshCredentials();
          }
        }
      }
    );
  }

  private generateScrapReport(from: string, to: string): Observable<any> {
    return new Observable(observer => {

      this.credentialService.lambda.invoke({
        FunctionName: environment.scrapReportFunction,
        Payload     : JSON.stringify({ from, to })
        }, (err, data) => {
          if (err) {
            if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
              this.authService.refreshCredentials();
            }
            observer.error(err);
          } else {
            const output = typeof data.Payload === 'string'
              ? JSON.parse(data.Payload)
              : data.Payload;

            observer.next(output);
          }
        }
      );
    });
  }

  createScrap(scrap: Scrap): void {
    const variables = { scrap };
    const query = `mutation($scrap: ScrapInput!) {
      createScrap(scrap: $scrap) {
        ${allScrapFields}
      }
    }`;

    this.invokeScrapMutation({ query, variables });
  }

  createScraps(scraps: Scrap[]): void {
    const variables = { scraps };
    const query = `mutation($scraps: [ScrapInput]) {
      createScraps(scraps: $scraps) {
        ${allScrapFields}
      }
    }`;

    this.invokeScrapMutation({ query, variables });
  }

  printScrapReport(from: string, to: string): void {
    this.store.dispatch({
      type: SET_SCRAP_REPORT_VIEW,
      payload: { downloadingScrapReport: true }
    });

    this.generateScrapReport(from, to)
      .take(1)
      .subscribe(
        url => {
          if (url) {
            const filename = `OA_Scrap_Report_${from}_${to}`;
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.style.display = 'none';
            console.log(`Downloading scrap report for ${from} to ${to}`);
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          }
        }, 
        err => {
          console.log(err);
          this.store.dispatch({
            type: SET_SCRAP_REPORT_VIEW,
            payload: { downloadingScrapReport: false }
          });
        },
        () => {
          this.store.dispatch({
            type: SET_SCRAP_REPORT_VIEW,
            payload: { downloadingScrapReport: false }
          });
        }
      );
  }

  getActiveScraps(): Observable<any> {
    const query = `{
      activeScraps {
        ${allScrapFields}
      }
    }`;

    return this.invokeScrap({ query });
  }

  getScrap(id: string): Observable<any> {
    const query = `{
      scrap(id: "${id}") {
        ${allScrapFields}
      }
    }`;

    return this.invokeScrap({ query });
  }

  getScrapByJobIDs(ids: string[]): Observable<any> {
    let formattedIDs = '';
    ids.forEach(id => formattedIDs += `"${id}", `);

    const query = `{
      scrapByJobIDs(ids: [${formattedIDs.trim()}]) {
        ${allScrapFields}
      }
    }`;

    return this.invokeScrap({ query });
  }

  getScrapHistory(category: string, from: string, to: string): Observable<any> {
    console.log(`scrap history from: ${from}`);
    console.log(`scrap history to: ${to}`);
    const query = `{
      scrapHistory(category: "${category}", from: "${from}", to: "${to}") {
        ${allScrapFields}
      }
    }`;

    return this.invokeScrap({ query });
  }

  getScraps(): Observable<any> {
    const query = `{
      scraps(param: "category", value: "${OA}") {
        ${allScrapFields}
      }
    }`;

    return this.invokeScrap({ query });
  }

  updateScrap(scrap: Scrap): void {
    const variables = { scrap };
    const query = `mutation ($scrap: ScrapInput!) {
      updateScrap(scrap: $scrap) {
        ${allScrapFields}
      }
    }`;

    this.invokeScrapMutation({ query, variables });
  }
}


// WEBPACK FOOTER //
// /Users/itu/Desktop/impreso_updated_sheets/impresocloud/www/client/src/app/services/scrap.service.ts